import {
  Brand,
  FishingReport,
  RecommendedProductFamily,
  RecommendedProductFamilyFacets,
  Subcategory,
  SubcatType,
} from '@omniafishing/core';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiV1 } from '../lib/api';
import { getIpCurrentSeasonGroup } from '../redux/geographic_location';
import {
  getBrandsNotHidden,
  getCategories,
  getSubcategories,
  getSubcatTypes,
} from '../redux/reference_data';
import { useUserPreferences } from './use_user_preferences';

export const useCatalogPage = ({
  recommendedFamilies,
  recommendedFamiliesFacets,
  selectedSpecies,
  fetchProductFamiliesFishingReport = true,
}: {
  recommendedFamilies: RecommendedProductFamily[];
  recommendedFamiliesFacets: RecommendedProductFamilyFacets;
  selectedSpecies: string[];
  fetchProductFamiliesFishingReport?: boolean;
}) => {
  const categories = useSelector(getCategories);
  const subcategories = useSelector(getSubcategories);
  const subcatTypes = useSelector(getSubcatTypes);
  const brands = useSelector(getBrandsNotHidden);
  const { userPreferencesWaterbodies } = useUserPreferences();
  const ipCurrentSeasonGroup = useSelector(getIpCurrentSeasonGroup);

  const [productFamiliesFishingReports, setProductFamiliesFishingReports] = useState<
    Record<string, FishingReport>
  >({});

  useEffect(() => {
    if (recommendedFamilies.length && fetchProductFamiliesFishingReport) {
      apiV1
        .productFamiliesFishingReportFetch({
          product_families: recommendedFamilies.map((rf) => rf.product_family.handle),
          waterbodies: userPreferencesWaterbodies.map((w) => w.id),
          season_group: ipCurrentSeasonGroup?.name,
          species: selectedSpecies,
        })
        .then((res) => {
          setProductFamiliesFishingReports(res.data.data);
        });
    }
  }, [
    recommendedFamilies,
    ipCurrentSeasonGroup,
    userPreferencesWaterbodies,
    selectedSpecies.join(),
  ]);

  const getProductSubcategories = (): (Subcategory & { count: number })[] => {
    const subcategoriesUnique = Object.keys(recommendedFamiliesFacets.subcategories);
    const subcategoriesWithCount = subcategoriesUnique.map((productSubcategory) => {
      const subcategory = subcategories.find((subcat) => subcat.name === productSubcategory);
      return {
        ...subcategory,
        count: recommendedFamiliesFacets.subcategories[subcategory.name],
      };
    });

    return _.sortBy(subcategoriesWithCount, 'display_name');
  };

  const getProductSubcatTypesWithCount = (): (SubcatType & { count: number })[] => {
    const productSubcatTypes = Object.keys(recommendedFamiliesFacets.subcat_types);

    const subcatTypesList = subcatTypes
      .filter((subcatType) => productSubcatTypes.includes(subcatType.name))
      .map((subcat_type) => {
        return {
          ...subcat_type,
          count: recommendedFamiliesFacets.subcat_types[subcat_type.name],
        };
      });

    return _.sortBy(subcatTypesList, 'display_name');
  };

  const getProductSubcategoriesInCategories = () => {
    const productSubcategories = getProductSubcategories();
    const subcatsInCategories = categories.map((category) => {
      const categorySubcategories = category.subcategories.map((subcat) => subcat.name);
      return {
        name: category.name,
        display_name: category.display_name,
        subcategories: productSubcategories.filter(
          (productSubcategory) => categorySubcategories.indexOf(productSubcategory.name) > -1
        ),
      };
    });

    return subcatsInCategories.filter((cat) => cat.subcategories.length);
  };

  const getProductSubcatTypesInSubcategories = () => {
    const productSubcatTypes = getProductSubcatTypesWithCount();
    const subcatTypesInSubcategories = getProductSubcategories().map((subcategory) => {
      const subcategorySubcatTypeNames = subcategory.subcat_types.map(
        (subcatType) => subcatType.name
      );
      return {
        name: subcategory.name,
        display_name: subcategory.display_name,
        count: subcategory.count,
        subcatTypes: productSubcatTypes.filter((productSubcatType) =>
          subcategorySubcatTypeNames.includes(productSubcatType.name)
        ),
      };
    });

    return subcatTypesInSubcategories;
  };

  const getBrandsWithCount = (): (Brand & { count: number })[] => {
    const facetBrandNames = Object.keys(recommendedFamiliesFacets.brands);

    const brandsList = brands
      .filter((brand) => facetBrandNames.includes(brand.name))
      .map((brand) => {
        return {
          ...brand,
          count: recommendedFamiliesFacets.brands[brand.name],
        };
      });

    return _.sortBy(brandsList, 'display_name');
  };

  return {
    productSubcategoriesInCategories: getProductSubcategoriesInCategories(),
    productSubcatTypesInSubcategories: getProductSubcatTypesInSubcategories(),
    brandsWithCount: getBrandsWithCount(),
    productFamiliesFishingReports,
  };
};
